import type { JsonDataWrapper, OdinDataRetrieval, OdinDataRetrievalOptions } from '@myosh/odin-components';
import { isDropDownNoSelection, Notification, OdinDataSender, OdinIcon } from '@myosh/odin-components';
import { useAsyncList } from '@react-stately/data';
import type { PointerEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormWorkflowStep } from '../../../../../@types/forms';
import type { RecordResult } from '../../../../../@types/records';
import useGridSettings from '../../../../../hooks/use-grid-settings';
import { useLazyGetRecordsQuery } from '../../../../../redux/services/record';
import { getDataGridComboboxFilters } from '../../../../../utils/data-grid-filters';
import { defaultGridSettings } from '../../../../dynamic-page/default-grid-settings';
import MobileCardsFilter from '../../../../dynamic-page/mobile-cards-filter';

import type { FieldFilters } from '../../../../../@types/common';
import { dateFormatBasedOnFilter } from '../../../../../utils/data-transformation-utils';
import { processFieldFilters } from '../../../../../utils/filter-url-builder-utils';
import MobileGridBase from '../../../../dynamic-page/mobile-grid-base';

import dot from 'dot-object';
import type { DynamicPageFilters } from '../../../../../redux/slices/dynamic-page-slice';
import { resolveFieldDisplayValue } from '../../../../../utils/field-property-util';
import TitleValueField from '../mobile/title-value-field';
import { getColumnsConfigurator, type LinkedRecordsSelectingProps } from './selection-utility';

export default function MobileLinkedRecordsSelecting({
  recordId,
  formId,
  value,
  linkedWorkflowSteps,
  formSettings,
  updatePreviewRecord,
  selectedLastEditedFilter,
  selectedConfig,
  selectedRecordsFilter,
  setSelectedRecords,
}: LinkedRecordsSelectingProps) {
  const linkedRecordsSelected = useRef<Array<string>>(value || []);

  const [userFilters, setUserFilters] = useState<FieldFilters>();
  const [getRecords, result] = useLazyGetRecordsQuery();

  const { t } = useTranslation();

  const filterData = useMemo<OdinDataRetrieval | undefined>(() => {
    return {
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldType === 'COMBOBOX') {
          await getDataGridComboboxFilters(
            subscriber,
            options,
            false,
            {
              selectedForm: { value: formSettings?.id, text: formSettings?.caption as string },
              workflowSteps: linkedWorkflowSteps,
            },
            formSettings
          );
        }
      },
    };
  }, [linkedWorkflowSteps, formSettings]);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      linkedRecordsSelected.current = value;
      setSelectedRecords(linkedRecordsSelected.current);
    }
  }, [value, setSelectedRecords]);

  const getColorElementIndicator = useCallback(
    (data?: RecordResult) => {
      const backgroundColor = linkedWorkflowSteps?.find(
        (step: ExtendedDynamicFormWorkflowStep) => step.label === data?.status
      )?.backgroundColor;
      return <div className="-ml-2 w-1 rounded" style={{ backgroundColor: backgroundColor }} />;
    },
    [linkedWorkflowSteps]
  );

  const addLinkedRecord = (itemId: unknown) => {
    if (itemId === undefined || itemId === null) {
      return;
    }
    const id = itemId.toString();
    const index = linkedRecordsSelected.current.findIndex((_id) => _id === id);
    if (index === -1) {
      linkedRecordsSelected.current = [...linkedRecordsSelected.current, id];
    } else {
      linkedRecordsSelected.current = linkedRecordsSelected.current.filter((recordId) => recordId !== id);
    }
    setSelectedRecords(linkedRecordsSelected.current);
  };

  const configId =
    selectedConfig && !isDropDownNoSelection(selectedConfig) ? (selectedConfig.value as number) : undefined;

  const viewFilter: DynamicPageFilters = useMemo(() => {
    return {
      viewConfig: isDropDownNoSelection(selectedConfig) ? undefined : selectedConfig,
      records: selectedRecordsFilter,
      lastEdited: selectedLastEditedFilter,
    };
  }, [selectedConfig, selectedLastEditedFilter, selectedRecordsFilter]);

  const {
    settings: gridSettings,
    error: gridSettingsError,
    loading: gridSettingsLoading,
  } = useGridSettings(defaultGridSettings, getColumnsConfigurator(), formId, configId, undefined, false, viewFilter);

  const list = useAsyncList<RecordResult>({
    initialSelectedKeys: new Set(value || []),
    async load({ cursor }) {
      if (selectedConfig === undefined || isDropDownNoSelection(selectedConfig) || gridSettings === undefined) {
        return { items: [] };
      }

      const parsedCursor = Number(cursor);
      let nextCursor: number | undefined = isNaN(parsedCursor) ? 1 : parsedCursor + 1;
      const filters = processFieldFilters(userFilters, dateFormatBasedOnFilter);

      const response = await getRecords({
        keyTypeId: true,
        formId: formId!,
        pageSize: 50,
        page: nextCursor,
        filters,
        sortDirective: '',
        excludedRecordId: recordId, // exclude the record being edited
      });
      if (!response?.data?.length || response.data.length < 50) {
        nextCursor = undefined;
      }

      const items = response.data?.map((item) => item.original) ?? [];
      if (nextCursor === undefined) {
        return { items };
      }
      return { items, cursor: String(nextCursor) };
    },
  });

  useEffect(() => {
    if (userFilters) {
      list.reload();
    }
  }, [userFilters]);

  const handlePreviewClick = useCallback((event: PointerEvent, item: RecordResult) => {
    event.stopPropagation();
    event.preventDefault();
    const recordId = item.id;
    updatePreviewRecord?.(recordId);
  }, []);

  const renderItem = (item: RecordResult) => {
    const displayColumns = gridSettings?.columns.filter((col) => col.visible)?.slice(0, 5) || [];
    return (
      <div className="group flex w-full flex-row">
        {getColorElementIndicator(item)}
        <div className="flex w-full min-w-0 flex-col items-start gap-4 rounded-lg p-2">
          <div className="flex w-full flex-wrap gap-x-4 gap-y-2">
            {item.title && <TitleValueField title={t('title')} value={item.title as string} key={`${item.id}-title`} />}
            <div
              onPointerDown={(event) => handlePreviewClick(event, item)}
              className="absolute right-0 top-0 text-primary-3"
            >
              <OdinIcon icon="Eye" />
            </div>
            {displayColumns
              .map((column) => {
                const value =
                  (column.valuePath ? dot.pick(column.valuePath, item) : item[column.field as keyof typeof item]) ?? '';
                const resolved = resolveFieldDisplayValue(value);
                const valueRendered = typeof resolved === 'object' ? JSON.stringify(resolved) : String(resolved);
                if (!valueRendered) {
                  return null;
                }
                const key = `${item.id}-${column.field}`;
                return <TitleValueField key={key} title={column.title || column.field} value={valueRendered} />;
              })
              .filter(Boolean)}
          </div>
        </div>
      </div>
    );
  };

  const isLoading = gridSettingsLoading || result.isLoading;
  return (
    <>
      {!gridSettingsError && (
        <div className="flex h-full">
          <div className="h-full w-full overflow-y-auto p-1">
            <MobileGridBase
              list={list}
              selectionHandler={(selectedItem) => {
                addLinkedRecord(selectedItem.id);
              }}
              formId={formId!}
              isLoading={isLoading}
              customRenderItem={renderItem}
              selectionMode="multiple"
            />
          </div>
          <MobileCardsFilter
            setUserFilters={setUserFilters}
            gridSettings={gridSettings}
            gridSettingsLoading={isLoading}
            filterData={filterData}
          />
        </div>
      )}
      {gridSettingsError && (
        <div className="flex h-full items-center justify-center">
          <Notification
            key="configNotification"
            title={t('error-config-title')}
            description={t('error-config-view-description')}
            type="error"
          />
        </div>
      )}
    </>
  );
}
